import { Dictionary } from '../../../../types/globals';
import { reduce, pluck } from '../../../common/utils/fp';
import {
    CategoryDataResponseFromApi,
    CategoryEntity,
    NormalizedEntitiesAndUrlKeys,
    SeoCategoryBreadCrumbItem,
} from './types';

const takeIdCatalogCategories = pluck('id_catalog_category');

const categoriesCollector = (
    parent: string | null = null,
    depthLevel: number = 1
): ((list: Array<CategoryDataResponseFromApi>) => NormalizedEntitiesAndUrlKeys) =>
    reduce(
        (acc: NormalizedEntitiesAndUrlKeys, category: CategoryDataResponseFromApi) => {
            const { entities = {}, urlKeys = {} } = acc;
            const {
                children = [],
                id_catalog_category: id,
                name,
                url_key: urlKey,
                category_nav_hidden: isHidden,
            } = category;
            const entity = {
                children: takeIdCatalogCategories(children),
                depthLevel,
                id,
                name,
                parent,
                urlKey,
                isHidden,
            };
            const flattenToEntitiesAndUrlKeys = categoriesCollector(id, depthLevel + 1);
            const normalizedChildrenCategories = flattenToEntitiesAndUrlKeys(children);

            return {
                entities: { ...entities, [id]: entity, ...normalizedChildrenCategories.entities },
                urlKeys: { ...urlKeys, [urlKey]: id, ...normalizedChildrenCategories.urlKeys },
            };
        },
        { entities: {}, urlKeys: {} }
    );

const flattenToEntitiesAndUrlKeys = categoriesCollector();
export const normalizeCategories = (categoriesData: CategoryDataResponseFromApi[]) => {
    const categories = takeIdCatalogCategories(categoriesData) as string[];
    const { entities, urlKeys } = flattenToEntitiesAndUrlKeys(categoriesData);
    return { categories, entities, urlKeys };
};

export const getEntityByUrlKey = (normalized: NormalizedEntitiesAndUrlKeys | null, urlKey: string) => {
    if (!urlKey || !normalized) {
        return null;
    }
    const { urlKeys = {}, entities = {} } = normalized;
    const categoryId = urlKeys[urlKey];
    return entities[categoryId] || null;
};

export const mapEntityToBreadCrumbItem = (
    entities: Dictionary<CategoryEntity>,
    cat: CategoryEntity | null
): SeoCategoryBreadCrumbItem | null => {
    if (!cat) {
        return cat as null;
    }
    const parent = cat.parent ? mapEntityToBreadCrumbItem(entities, entities[cat.parent]) : null;
    return {
        depthLevel: cat.depthLevel + 1,
        name: cat.name,
        url: cat.urlKey,
        parent,
    };
};
